import { useCallback, useMemo, useState } from 'react';
import {
    getCurrentUser,
    getUserSpecializations,
    createUserSpecialization,
    deleteUserSpecialization,
    getUserSpecializationGrades,
    createUserSpecializationGrade,
    deleteUserSpecializationGrade,
} from '../api/users_institutions';

// Each group maps to its own UserSpecialization.target_domain. A teacher can have grades
// checked across several groups at once (e.g. Middle School and High School).
export const GRADE_GROUPS = [
    { domain: 'Kindergarten', labelKey: 'specializationSelector.kindergarten', grades: ['KG 1', 'KG 2', 'KG 3'] },
    { domain: 'Elementary School', labelKey: 'specializationSelector.elementarySchool', grades: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6'] },
    { domain: 'Middle School', labelKey: 'specializationSelector.middleSchool', grades: ['Grade 7', 'Grade 8', 'Grade 9'] },
    { domain: 'High School', labelKey: 'specializationSelector.highSchool', grades: ['Grade 10', 'Grade 11', 'Grade 12'] },
];

export const emptyGroupSelection = () => Object.fromEntries(GRADE_GROUPS.map((group) => [group.domain, new Set()]));

/**
 * Shared grade-selection state and backend sync logic for a user's school specializations.
 * Used by both SpecializationSelector.jsx (the uni/school/verify modal flow) and Settings.jsx's
 * inline "Manage Grades" picker, so the two never drift apart on how a selection gets saved.
 */
export function useSpecializationGrades() {
    // domain -> { specializationId, gradeMap: Map<grade, gradeId> } as loaded from the backend --
    // the diff baseline saveSelection() compares against.
    const [initialGroups, setInitialGroups] = useState({});
    // domain -> Set<grade> reflecting the current checkbox state
    const [selectedGroups, setSelectedGroups] = useState(emptyGroupSelection());

    const loadCurrentGrades = useCallback(async () => {
        const user = getCurrentUser();
        if (!user?.id) return null;

        const specializations = await getUserSpecializations(user.id);
        const schoolSpecializations = specializations.filter(
            (spec) => String(spec.target_domain || '').trim().toLowerCase() !== 'university'
        );

        const nextInitialGroups = {};
        const nextSelectedGroups = emptyGroupSelection();

        for (const group of GRADE_GROUPS) {
            const specialization = schoolSpecializations.find(
                (spec) => String(spec.target_domain || '').trim().toLowerCase() === group.domain.toLowerCase()
            );

            if (!specialization) {
                nextInitialGroups[group.domain] = { specializationId: null, gradeMap: new Map() };
                continue;
            }

            const grades = await getUserSpecializationGrades(user.id, specialization.id);
            const gradeMap = new Map((Array.isArray(grades) ? grades : []).map((grade) => [grade.grade, grade.id]));
            nextInitialGroups[group.domain] = { specializationId: specialization.id, gradeMap };
            nextSelectedGroups[group.domain] = new Set(gradeMap.keys());
        }

        setInitialGroups(nextInitialGroups);
        setSelectedGroups(nextSelectedGroups);
        return { initialGroups: nextInitialGroups, selectedGroups: nextSelectedGroups, schoolSpecializations };
    }, []);

    const toggleGrade = useCallback((domain, grade) => {
        setSelectedGroups((prev) => {
            const nextSet = new Set(prev[domain]);
            if (nextSet.has(grade)) {
                nextSet.delete(grade);
            } else {
                nextSet.add(grade);
            }
            return { ...prev, [domain]: nextSet };
        });
    }, []);

    const totalSelectedGrades = useMemo(
        () => Object.values(selectedGroups).reduce((sum, set) => sum + set.size, 0),
        [selectedGroups]
    );

    // Persists `selectedGroups` against whatever `initialGroups` currently holds (the last
    // loadCurrentGrades() snapshot), creating/deleting UserSpecialization and
    // UserSpecializationGrade rows only for what actually changed.
    const saveSelection = useCallback(async () => {
        const user = getCurrentUser();
        if (!user?.id) return;

        for (const group of GRADE_GROUPS) {
            const domain = group.domain;
            const initial = initialGroups[domain] || { specializationId: null, gradeMap: new Map() };
            const selected = selectedGroups[domain] || new Set();
            const initialGradeStrings = new Set(initial.gradeMap.keys());

            const addedGrades = [...selected].filter((grade) => !initialGradeStrings.has(grade));
            const removedGrades = [...initialGradeStrings].filter((grade) => !selected.has(grade));

            if (addedGrades.length === 0 && removedGrades.length === 0) {
                continue;
            }

            let specializationId = initial.specializationId;

            if (selected.size > 0 && !specializationId) {
                const created = await createUserSpecialization(user.id, {
                    user: user.id,
                    target_domain: domain,
                });
                specializationId = created.id;
            }

            if (!specializationId) {
                continue;
            }

            for (const grade of addedGrades) {
                await createUserSpecializationGrade(user.id, specializationId, {
                    user_specialization: specializationId,
                    grade,
                });
            }

            for (const grade of removedGrades) {
                const gradeId = initial.gradeMap.get(grade);
                if (gradeId) {
                    await deleteUserSpecializationGrade(user.id, specializationId, gradeId);
                }
            }

            if (selected.size === 0) {
                await deleteUserSpecialization(user.id, specializationId);
            }
        }
    }, [initialGroups, selectedGroups]);

    return {
        initialGroups,
        setInitialGroups,
        selectedGroups,
        setSelectedGroups,
        toggleGrade,
        totalSelectedGrades,
        loadCurrentGrades,
        saveSelection,
    };
}
